import { useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronUp } from "lucide-react";
import { cn } from "@/lib/cn";
import { api } from "@/lib/api";
import { NAV, isNavItemActive, navHref, type BadgeKey, type NavItem } from "@/app/nav";
import { useAuth } from "@/app/auth";
import { DigiSubsMark } from "@/components/brand/logos";
import { NewBadge } from "@/components/ui";
import { useRealtime } from "@/lib/socket";

const COLLAPSE_KEY = "ds.nav.collapsed";

function readCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(window.localStorage.getItem(COLLAPSE_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

export function useNavBadges() {
  const { me } = useAuth();
  const query = useQuery({
    queryKey: ["nav-badges", me?.current?.clientId],
    queryFn: () => api.get<Partial<Record<BadgeKey, number>>>("/analytics/badges").catch((): Partial<Record<BadgeKey, number>> => ({})),
    enabled: Boolean(me?.current),
    refetchInterval: 60_000,
    staleTime: 20_000,
  });
  useRealtime("message.created", () => void query.refetch(), Boolean(me?.current));
  useRealtime("order.created", () => void query.refetch(), Boolean(me?.current));
  return query.data ?? ({} as Partial<Record<BadgeKey, number>>);
}

export function Logo() {
  return (
    <div className="flex items-center gap-2.5 px-1">
      <DigiSubsMark size={40} />
      <div className="leading-none">
        <p className="text-[19px] font-bold tracking-[0.02em] text-white">DIGISUBS</p>
        <p className="mt-1 text-[10px] font-semibold tracking-[0.3em] text-[#9aa6c0]">BUSINESS OS</p>
      </div>
    </div>
  );
}

function Item({ item, active, badge, onNavigate }: { item: NavItem; active: boolean; badge?: number; onNavigate?: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={navHref(item)}
      end={item.to === "/"}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        // Touch rows in the drawer, the dense rows of the screenshots on desktop.
        "group flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13px] transition-colors lg:h-7",
        active
          ? "bg-gradient-to-r from-[#1d4ed8] to-[#2563eb] font-medium text-white shadow-[0_0_0_1px_rgb(59_130_246/0.55),0_4px_18px_-2px_rgb(37_99_235/0.7)]"
          : "text-[#c8d0e2] hover:bg-white/[0.045] hover:text-white",
      )}
    >
      <Icon className={cn("size-4 shrink-0", active ? "text-white" : "text-[#a9b4cc] group-hover:text-white")} strokeWidth={1.75} />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.isNew ? <NewBadge /> : null}
      {badge ? (
        <span className={cn("inline-flex h-[18px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold leading-none", active ? "bg-white/25 text-white" : "bg-[#ef4444] text-white")}>
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { me, can } = useAuth();
  const { pathname, search } = useLocation();
  const badges = useNavBadges();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed);

  const toggle = (id: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        window.localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const sections = NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.platformAdminOnly) return Boolean(me?.user.isPlatformAdmin);
      return !item.permission || can(item.permission);
    }),
  })).filter((s) => s.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 shrink-0 items-center px-4">
        <Logo />
      </div>
      <nav className="sidebar-scroll min-h-0 flex-1 overflow-y-auto pb-6 pl-3 pr-2 pt-1" aria-label="Navigation principale">
        {sections.map((section) => {
          const current = section.items.some((i) => isNavItemActive(i, pathname, search));
          const isCollapsed = collapsed[section.id] && !current;
          return (
            <div key={section.id} className="mb-1.5">
              <button
                type="button"
                onClick={() => toggle(section.id)}
                aria-expanded={!isCollapsed}
                className={cn(
                  "flex w-full items-center justify-between px-2.5 pb-1 pt-2.5 text-[10.5px] font-bold uppercase tracking-[0.13em] transition-colors",
                  current ? "text-[#3d9bff]" : "text-[#b4bed4] hover:text-white",
                )}
              >
                {section.title}
                <ChevronUp className={cn("size-3.5 text-[#7d8aa6] transition-transform", isCollapsed && "rotate-180")} />
              </button>
              {!isCollapsed ? (
                <div className="space-y-px">
                  {section.items.map((item) => (
                    <Item key={navHref(item)} item={item} active={isNavItemActive(item, pathname, search)} badge={item.badge ? badges[item.badge] : undefined} onNavigate={onNavigate} />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export function Sidebar({ className }: { className?: string }) {
  return (
    <aside className={cn("fixed inset-y-0 left-0 z-40 w-[240px] flex-col border-r border-[#141d33] bg-sidebar", className)}>
      <SidebarContent />
    </aside>
  );
}
